define('presentation/signup/viewModels/signupPlansViewModel',[
    'businessServices/router/router',
    'businessServices/signup/signupEntryGate',
    'common/promises/promiseFactory',
    'common/storage/workSessionIdStorage',
    'constants/billingPackageTypeConstants',
    'presentation/signup/common/signupNavigationManager',
    'presentation/signup/facades/signupPlansFacade',
    'presentation/signup/viewModels/signupPlansMultiSelectViewModel',
    'presentation/signup/viewModels/signupPlansTelephonyViewModel',
], function () {
    return function () {
        const self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const _i18n = require('i18next');

        const _workSessionIdStorage = require('common/storage/workSessionIdStorage');
        const _billingPackageTypes = require('constants/billingPackageTypeConstants');

        const _router = require('businessServices/router/router');

        let _facade = null;
        let _signupEntryGate = null;
        let _signupNavigationManager = null;

        const _isSignupForCustomer = _workSessionIdStorage.getWorkSessionId() != null;

        self.isCompositionComplete = ko.observable(false);
        self.isScrollActive = ko.observable(false);
        self.activeChildViewModel = ko.observable(null);

        self.planVariant = ko.observable('MultiSelect');

        const _childValue = (name) => {
            const childViewModel = self.activeChildViewModel();
            return childViewModel === null ? undefined : ko.unwrap(childViewModel[name]);
        };

        self.isWideTableColumn = ko.pureComputed(() => _childValue('isWideTableColumn') === true);
        self.isWideCardColumn = ko.pureComputed(() => _childValue('isWideCardColumn') === true);

        self.headingText = ko.pureComputed(() => {
            return _childValue('headingText') || _i18n.t('signupPlans:headingText' + self.planVariant());
        });
        self.subtitleText = ko.pureComputed(() => {
            return _childValue('subtitleText') || _i18n.t('signupPlans:subtitleText' + self.planVariant());
        });

        const _isTelephonyOnly = (availablePackages) => {
            return !availablePackages.some(p =>
                p.type === _billingPackageTypes.liveReceptionist.id ||
                p.type === _billingPackageTypes.webChat.id ||
                p.type === _billingPackageTypes.outboundCalling.id
            );
        };

        const _savePackageByType = (type, packageId) => {
            switch (type) {
                case _billingPackageTypes.telephony.id:
                    return _facade.saveTelephonyPackage(packageId);
                case _billingPackageTypes.liveReceptionist.id:
                    return _facade.saveLiveReceptionistPackage(packageId);
                case _billingPackageTypes.webChat.id:
                    return _facade.saveWebChatPackage(packageId);
                case _billingPackageTypes.outboundCalling.id:
                    return _facade.saveOutboundCallingPackage(packageId);
            }
        };

        // Telephony-only customers pick their own number on the number screens; agents always do.
        // Everyone else self-service has one assigned at payment, so their phone step is marked
        // satisfied here rather than by a reservation.
        const _reconcileNumberSelection = () => {
            return _promiseFactory.defer((deferredObject) => {
                const picksOwnNumber = _isSignupForCustomer || _facade.hasOnlyTelephonyPackage();

                if (picksOwnNumber) {
                    deferredObject.resolve();
                    return;
                }

                _facade.setPhoneNumberCompleted(true)
                    .fail(deferredObject.reject)
                    .done(() => deferredObject.resolve());
            });
        };

        const PACKAGE_QUERY_PARAMETERS = [
            { queryParameter: "package_lr", type: _billingPackageTypes.liveReceptionist.id },
            { queryParameter: "package_wc", type: _billingPackageTypes.webChat.id },
            { queryParameter: "package_obc", type: _billingPackageTypes.outboundCalling.id }
        ];

        // Deep-linked package ids are applied here rather than on the route, because the eligible
        // list is derived from the affiliate server-side and is not known until it has been
        // fetched. An id that is not in the list - a stale link, or a package this affiliate cannot
        // sell - is dropped, and the customer picks from what they are actually offered.
        const _applyDeepLinkedPackages = (availablePackages) => {
            return _promiseFactory.defer((deferredObject) => {
                const activeInstruction = _router.activeInstruction();
                const queryParams = activeInstruction ? activeInstruction.queryParams : null;

                if (!queryParams) {
                    deferredObject.resolve();
                    return;
                }

                const savePromiseFactory = new PromiseFactoryConstructor();
                let isAnyPackageApplied = false;

                PACKAGE_QUERY_PARAMETERS.forEach(({ queryParameter, type }) => {
                    if (queryParams.hasOwnProperty(queryParameter) === false) {
                        return;
                    }

                    const packageId = queryParams[queryParameter];
                    const isEligible = availablePackages
                        .some(availablePackage => availablePackage.packageId === packageId &&
                            availablePackage.type === type);

                    if (isEligible === false) {
                        return;
                    }

                    isAnyPackageApplied = true;
                    savePromiseFactory.defer((savePromise) => {
                        _savePackageByType(type, packageId)
                            .fail(savePromise.reject)
                            .done(() => savePromise.resolve());
                    });
                });

                savePromiseFactory.wait()
                    .fail(deferredObject.reject)
                    .done(() => {
                        if (isAnyPackageApplied) {
                            _facade.setPackageDeepLinked();
                        }
                        deferredObject.resolve();
                    });
            });
        };

        const _setPlansCompleted = () => {
            return _promiseFactory.defer((deferredObject) => {
                _facade.setPlansCompleted(true)
                    .fail(deferredObject.reject)
                    .done(() => {
                        _reconcileNumberSelection()
                            .fail(deferredObject.reject)
                            .done(() => {
                                deferredObject.resolve(true);
                            });
                    });
            });
        };

        const _navigateToPreviousPage = () => {
            return _promiseFactory.defer((deferredObject) => {
                _signupNavigationManager.previousPage();
                deferredObject.resolve();
            });
        };

        const _activateTelephonyViewModel = (availablePackages, sessionData) => {
            const TelephonyViewModelConstructor = require('presentation/signup/viewModels/signupPlansTelephonyViewModel');
            const telephonyViewModel = new TelephonyViewModelConstructor();
            const telephonyPackage = availablePackages.find(p => p.packageId === sessionData.telephonyPackageId);
            const activatePromise = telephonyViewModel.activate({
                telephonyPackage,
                selectedPackageId: sessionData.telephonyPackageId,
                affiliateId: sessionData.affiliateId,
                onPackageSelected: (p) => _savePackageByType(_billingPackageTypes.telephony.id, p.packageId),
                onForwardArrowClicked: _setPlansCompleted,
                onBackArrowClicked: _navigateToPreviousPage,
            });
            self.planVariant('Telephony');
            self.activeChildViewModel(telephonyViewModel);

            return activatePromise;
        };

        const _activateMultiSelectViewModel = (availablePackages, sessionData) => {
            const MultiSelectViewModelConstructor = require('presentation/signup/viewModels/signupPlansMultiSelectViewModel');
            const multiSelectViewModel = new MultiSelectViewModelConstructor();
            const activatePromise = multiSelectViewModel.activate({
                availablePackages,
                isPreviousPageValid: _signupNavigationManager.isPreviousPageValid(),
                isPackageDeepLinked: _facade.isPackageDeepLinked(),
                selectedPackageIds: {
                    liveReceptionistPackageId: sessionData.liveReceptionistPackageId,
                    webChatPackageId: sessionData.webChatPackageId,
                    outboundCallingPackageId: sessionData.outboundCallingPackageId,
                },
                onPackageSelected: (type, packageId) => _savePackageByType(type, packageId),
                onPackageRemoved: (type) => _savePackageByType(type, null),
                onForwardArrowClicked: _setPlansCompleted,
                onBackArrowClicked: (isOnPlansView) => {
                    return _promiseFactory.defer((deferredObject) => {
                        if (isOnPlansView) {
                            _signupNavigationManager.previousPage();
                        }
                        deferredObject.resolve();
                    });
                },
            });
            self.planVariant('MultiSelect');
            self.activeChildViewModel(multiSelectViewModel);

            return activatePromise;
        };

        const _onSignupAffiliateUpdated = () => {
            return _promiseFactory.deferIndefinitely((promise) => {
                _facade.getBillingPackages()
                    .fail(promise.reject)
                    .done(({ billingPackages }) => {
                        _facade.loadSignupSession()
                            .fail(promise.reject)
                            .done((sessionData) => {
                                _activateChildViewModel(billingPackages, sessionData)
                                    .fail(promise.reject)
                                    .done(() => promise.resolve());
                            });
                    });
            });
        };

        const _activateChildViewModel = (availablePackages, sessionData) => {
            if (_isTelephonyOnly(availablePackages)) {
                return _activateTelephonyViewModel(availablePackages, sessionData);
            }

            return _activateMultiSelectViewModel(availablePackages, sessionData);
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.detached = () => {
            _signupNavigationManager.detached();
            _facade.dispose();
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/signup/facades/signupPlansFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const SignupEntryGateConstructor = require('businessServices/signup/signupEntryGate');
            _signupEntryGate = new SignupEntryGateConstructor();
            _signupEntryGate.init();

            const SignupNavigationManagerConstructor = require('presentation/signup/common/signupNavigationManager');
            _signupNavigationManager = new SignupNavigationManagerConstructor();
            _signupNavigationManager.init();

            return _initialize();
        };

        const _initialize = () => {
            _promiseFactory.defer((beginEntryGatePromise) => {
                _signupEntryGate.begin()
                    .fail(beginEntryGatePromise.resolve)
                    .done(beginEntryGatePromise.resolve);
            });

            _promiseFactory.deferIndefinitely((initializePromise) => {
                _facade.getBillingPackages()
                    .fail(initializePromise.reject)
                    .done((result) => {
                        _applyDeepLinkedPackages(result.billingPackages)
                            .fail(initializePromise.reject)
                            .done(() => {
                                _facade.loadSignupSession()
                                    .fail(initializePromise.reject)
                                    .done((sessionData) => {
                                        _activateChildViewModel(result.billingPackages, sessionData)
                                            .fail(initializePromise.reject)
                                            .done(() => {
                                                _facade.onSignupAffiliateUpdated(_onSignupAffiliateUpdated);
                                                initializePromise.resolve();
                                            });
                                    });
                            });
                    });
            });

            return _promiseFactory.wait();
        };
    };
});
